import React from 'react';
import * as R from 'ramda';
import classNames from 'classnames';
import Button from 'components/general/Button/Button';
import Icon from 'components/general/Icon/Icon';
import ListNav, {
  ListNavItemPropDef,
  ListNavPropDef,
} from 'components/general/ListNav/ListNav';
import withCollapsible, {
  withCollapsibleDefs,
} from 'components/hoc/withCollapsible/withCollapsible';

/**
 * Dumb nav component without handlers
 */
const SubNavDumb: React.SFC<ListNavPropDef & withCollapsibleDefs> = ({
  items,
  currentID,
  isOpen,
  onClose,
  onToggle,
}) => {
  const navTitle = getCurrentNavTitle(items, currentID);

  return (
    <div
      className={classNames('sub-nav', {
        'sub-nav--is-open': isOpen,
      })}
    >
      <Button
        className="sub-nav__trigger"
        types={['box']}
        onClick={onToggle}
      >
        <span className="sub-nav__trigger-label">
          {navTitle.parent && (
            <span className="sub-nav__parent-title">
              {navTitle.parent} &nbsp;
            </span>
          )}
          <span className="sub-nav__title">
            {navTitle.title || 'Menu'}
          </span>
        </span>
        <Icon
          className="sub-nav__trigger-icon"
          name={`chevron-${isOpen ? 'up' : 'down'}`}
          types={['no-pull', 'push-left']}
        />
      </Button>
      <div className="sub-nav__menu">
        <ListNav
          items={items}
          currentID={currentID}
          onItemClick={onClose}
        />
      </div>
    </div>
  );
};

SubNavDumb.defaultProps = {
  items: [],
};

const flattenWithParent = (parentTitle?: string) => (
  item: ListNavItemPropDef,
): ListNavItemPropDef[] => ([
  (
    parentTitle
      ? R.set(R.lensProp('parentTitle'), parentTitle, item)
      : item
  ),
  ...R.chain(
    flattenWithParent(R.prop('title', item)),
    R.propOr([], 'children', item),
  ),
]);

/**
 * Retrieves the title of the selected element (regardless of if it's child
 * or parent.
 */
const getCurrentNavTitle = (
  items: ListNavItemPropDef[],
  currentID: string|number,
): currentItemType => R.pipe(
  // Flatten the items so nested children live at the same level as the
  // parents, tracking the immediate parent's title on each child
  R.chain(flattenWithParent()),
  R.find(R.propEq('id', currentID)),
  R.applySpec<currentItemType>({
    title: R.prop('title'),
    parent: R.prop('parentTitle'),
  }),
)(items);

interface currentItemType {
  title: string;
  parent?: string;
}

/**
 * SubNav with handlers
 */
const SubNav: React.SFC<ListNavPropDef> = withCollapsible()(SubNavDumb);

export default SubNav;

